import { Shield, ChevronDown, ChevronUp, Car, DollarSign } from 'lucide-react';

export type BadgeTier = 'friend' | 'family' | 'fam' | 'legend';

export interface InvestorBadgeProps {
  tier: BadgeTier;
  carsCount: number;
  totalEquity: number;
  showDetails: boolean;
  onToggleDetails: () => void;
  size?: 'sm' | 'md' | 'lg';
}

const TIER_CONFIG: Record<
  BadgeTier,
  { color: string; bg: string; border: string; label: string; textColor: string; ringColor: string }
> = {
  friend: {
    color: '#f43f5e',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    label: 'Friend',
    textColor: 'text-rose-400',
    ringColor: 'ring-rose-400/30',
  },
  family: {
    color: '#f97316',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    label: 'Family',
    textColor: 'text-orange-400',
    ringColor: 'ring-orange-400/30',
  },
  fam: {
    color: '#6366f1',
    bg: 'bg-indigo-500/10',
    border: 'border-indigo-500/30',
    label: 'FAM',
    textColor: 'text-indigo-400',
    ringColor: 'ring-indigo-400/30',
  },
  legend: {
    color: '#eab308',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/30',
    label: 'Legend',
    textColor: 'text-yellow-400',
    ringColor: 'ring-yellow-400/50',
  },
};

/**
 * Returns the investor badge tier based on contribution count and total contributed,
 * mirroring the DB trigger logic in angel_badges.
 *   legend : 15+ contributions OR $10,000+
 *   fam    : 7+ contributions OR $2,000+
 *   family : 3+ contributions OR $500+
 *   friend : everyone else
 */
export function getBadgeTierFromDB(contributionCount: number, totalContributed: number): BadgeTier {
  if (contributionCount >= 15 || totalContributed >= 10_000) return 'legend';
  if (contributionCount >= 7  || totalContributed >= 2_000)  return 'fam';
  if (contributionCount >= 3  || totalContributed >= 500)    return 'family';
  return 'friend';
}

export function InvestorBadge({
  tier,
  carsCount,
  totalEquity,
  showDetails,
  onToggleDetails,
  size = 'md',
}: InvestorBadgeProps) {
  const cfg = TIER_CONFIG[tier];

  const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
  const textSize = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';
  const paddingCls = size === 'sm' ? 'px-2.5 py-1' : size === 'lg' ? 'px-4 py-2.5' : 'px-3 py-1.5';
  const chevronSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  const shieldStyle = { color: cfg.color, fill: `${cfg.color}22` };

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button
        onClick={onToggleDetails}
        className={`inline-flex items-center gap-2 rounded-full border ${cfg.bg} ${cfg.border} ${paddingCls} ring-2 ${cfg.ringColor} transition-all hover:brightness-95 active:scale-95 cursor-pointer select-none${tier === 'legend' ? ' ring-yellow-400/50' : ''}`}
        title={showDetails ? 'Hide details' : 'Show details'}
      >
        <Shield
          className={`${iconSize} flex-shrink-0`}
          style={shieldStyle}
        />
        <span className={`font-semibold ${textSize} ${cfg.textColor}`}>{cfg.label}</span>
        {showDetails ? (
          <ChevronUp className={`${chevronSize} ${cfg.textColor} opacity-60`} />
        ) : (
          <ChevronDown className={`${chevronSize} ${cfg.textColor} opacity-60`} />
        )}
      </button>

      {showDetails && (
        <div
          className={`flex items-center gap-3 rounded-xl border ${cfg.border} ${cfg.bg} px-3 py-2 shadow-sm`}
        >
          <span className={`flex items-center gap-1 ${textSize} ${cfg.textColor} font-medium`}>
            <Car className="w-3.5 h-3.5 opacity-70" />
            {carsCount} {carsCount === 1 ? 'car' : 'cars'}
          </span>
          <span className="text-gray-300">·</span>
          <span className={`flex items-center gap-1 ${textSize} ${cfg.textColor} font-medium`}>
            <DollarSign className="w-3.5 h-3.5 opacity-70" />
            {totalEquity.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} total equity
          </span>
        </div>
      )}
    </div>
  );
}
